// crewbit-admin/src/pages/Money/InvoicesPageBranches.tsx
//
// Extracted state-branch sub-components for MoneyInvoicesPage. Lives next
// to the page rather than under components/money because each branch is
// tightly coupled to the page's loading/error/no-business state model and
// is not reused elsewhere.
//
// Split out from Invoices.tsx to satisfy Golden Rule #14 (file <500 LoC).

import type { ReactNode } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { MarkPaidModal } from '../../components/money/MarkPaidModal';
import { SendInvoiceModal } from '../../components/money/SendInvoiceModal';
import { RefundModal } from '../../components/money/RefundModal';
import { CancelInvoiceModal } from '../../components/money/CancelInvoiceModal';
import SettingsGearLink from '../../components/money/settings/SettingsGearLink';
import { admin, palette, radii, shadow, spacing } from '../../styles/tokens';
import type { InvoiceRow } from '../../types';

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

const cardStyle: React.CSSProperties = {
  background: palette.white,
  borderRadius: admin.cardRadius,
  border: `1px solid ${admin.border}`,
  boxShadow: shadow.card,
  overflow: 'hidden',
};

interface BranchScaffoldProps {
  headerActions?: ReactNode;
  children: ReactNode;
}

function BranchScaffold({ headerActions, children }: BranchScaffoldProps) {
  return (
    <div style={pageStyle}>
      <PageHeader
        title="Invoices" back={{ to: '/money', label: 'Money' }}
        primaryAction={headerActions}
        secondaryActions={<SettingsGearLink />}
      />
      {children}
    </div>
  );
}

interface SlideOversProps {
  detailPanel: ReactNode;
  authoringPanel: ReactNode;
}

export function NoBusinessBranch({ detailPanel, authoringPanel }: SlideOversProps) {
  return (
    <BranchScaffold>
      <EmptyState
        title="Pick a business"
        subtitle="Switch to an active business from the sidebar to see invoices."
      />
      {detailPanel}
      {authoringPanel}
    </BranchScaffold>
  );
}

interface LoadingBranchProps extends SlideOversProps {
  headerActions: ReactNode;
}

export function LoadingBranch({ headerActions, detailPanel, authoringPanel }: LoadingBranchProps) {
  return (
    <BranchScaffold headerActions={headerActions}>
      <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.lg }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '16px 18px',
              background: palette.white,
              border: `1px solid ${admin.border}`,
              borderRadius: admin.cardRadius,
            }}
          >
            <Skeleton width={80} height={10} />
            <div style={{ height: 12 }} />
            <Skeleton width={120} height={26} />
            <div style={{ height: 8 }} />
            <Skeleton width={60} height={10} />
          </div>
        ))}
      </div>
      <div style={{ ...cardStyle, padding: spacing.md }}>
        <TableSkeleton rows={5} columns={7} />
      </div>
      {detailPanel}
      {authoringPanel}
    </BranchScaffold>
  );
}

interface ErrorBranchProps extends SlideOversProps {
  headerActions: ReactNode;
  error: string;
  onRetry: () => void;
}

export function ErrorBranch({ headerActions, error, onRetry, detailPanel, authoringPanel }: ErrorBranchProps) {
  return (
    <BranchScaffold headerActions={headerActions}>
      <div
        style={{
          background: palette.amberWash,
          border: `1px solid ${palette.amber}`,
          borderRadius: admin.cardRadius,
          padding: spacing.lg,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.md,
          boxShadow: shadow.card,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.amber }}>
            Couldn't load invoices.
          </div>
          <div style={{ fontSize: 13, color: palette.amber, marginTop: 4 }}>{error}</div>
        </div>
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: '8px 14px',
            borderRadius: radii.sm,
            border: 'none',
            background: palette.amber,
            color: palette.white,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
      {detailPanel}
      {authoringPanel}
    </BranchScaffold>
  );
}

export type RowModal =
  | { kind: 'mark_paid'; invoice: InvoiceRow }
  | { kind: 'send'; invoice: InvoiceRow }
  | { kind: 'refund'; invoice: InvoiceRow }
  | { kind: 'cancel'; invoice: InvoiceRow }
  | null;

interface RowActionModalsProps {
  rowModal: RowModal;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

export function RowActionModals({ rowModal, onClose, onResult }: RowActionModalsProps) {
  if (!rowModal) return null;
  switch (rowModal.kind) {
    case 'mark_paid':
      return (
        <MarkPaidModal
          isOpen
          invoiceId={rowModal.invoice.id}
          defaultAmount={rowModal.invoice.total}
          onClose={onClose}
          onResult={onResult}
        />
      );
    case 'send':
      return (
        <SendInvoiceModal
          isOpen
          invoiceId={rowModal.invoice.id}
          clientEmail={rowModal.invoice.client_email}
          onClose={onClose}
          onResult={onResult}
        />
      );
    case 'refund':
      // alreadyRefunded passed as 0 — useInvoices SELECT doesn't return
      // refund_amount. Substrate enforces the cap; if operator surfaces
      // partial-refund context confusion, file §Admin.InvoicesListRefundContext.
      return (
        <RefundModal
          isOpen
          invoiceId={rowModal.invoice.id}
          total={rowModal.invoice.total}
          alreadyRefunded={0}
          onClose={onClose}
          onResult={onResult}
        />
      );
    case 'cancel':
      return (
        <CancelInvoiceModal
          isOpen
          invoiceId={rowModal.invoice.id}
          onClose={onClose}
          onResult={onResult}
        />
      );
  }
}
